import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { ConfirmDialogComponent } from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog.component';
import {
    ConfirmDialogInput,
    ConfirmDialogResult,
} from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog';
import { MatDialog } from '@angular/material/dialog';
import { Tierarzt } from '../../../../projects/kiene-core/src/lib/components/tieraerzte/classes/tierarzt';

@Component({
    selector: 'kiene-evaluationsbogen-schwein',
    templateUrl: './evaluationsbogen-schwein.component.html',
    styleUrls: ['./evaluationsbogen-schwein.component.scss'],
})
export class EvaluationsbogenSchweinComponent implements OnInit, OnDestroy {
    form: FormGroup = this.fb.group({
        evaluationsJahr: [null as number | null],
        betrieb: this.fb.group({
            name: [''],
            vvvo: [''],
            tskNummer: [''],
        }),
        tierarzt: this.fb.group({
            name: [''],
            praxis: [''],
            registriernummern: [''],
        }),
        berater: this.fb.group({
            name: [''],
            registriernummern: [''],
        }),
        datumBeratung: [null as Date | null],
        ablaufeLageskizzeAktuell: [null as boolean | null],
        handlungsempfehlungenUmgesetzt: [null as boolean | null],
        zusaetzlicheEmpfehlungen: [null as boolean | null],
        handlungsempfehlungen: this.fb.array([this.createHandlungRow()]),
        beratungszeit: this.fb.group({
            tierarztStunden: [''],
            beraterStunden: [''],
        }),
        beihilfeBeantragen: [true],
    });

    ebId = 0;
    statusId = 231;
    kundeId: number | null = null;
    tierarztId: number | null = null;
    currentVet: Tierarzt | null = null;
    vorgaengerBsId: number | null = null;
    planKuid: string | null = null;
    planBeraterKuid: string | null = null;
    unterschriftKunde = false;
    unterschriftBerater = false;
    isSaving = false;
    isLoading = false;
    planUpdateUploads: Array<{ bsd_id?: number; pdf_id?: number; dateiname: string; sektion?: string }> = [];
    private suppressAutosave = false;
    private autosaveHandle: number | undefined;
    private formSub: Subscription | undefined;
    private pendingSectionIds = new Set<number>();

    constructor(
        private readonly fb: FormBuilder,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly api: KieneBackendApiService,
        private readonly messageService: MessageService,
        private readonly localStorageService: LocalStorageService,
        private readonly pdf: PdfViewerDesktopService,
        private readonly dialog: MatDialog,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    get handlungsempfehlungen(): FormArray {
        return this.form.get('handlungsempfehlungen') as FormArray;
    }

    ngOnInit(): void {
        this.formSub = this.form.valueChanges.subscribe(() => {
            if (this.suppressAutosave || this.isReadOnly()) {
                return;
            }
            this.pendingSectionIds.add(1);
            this.pendingSectionIds.add(2);
            this.pendingSectionIds.add(3);
            if (this.autosaveHandle) {
                window.clearTimeout(this.autosaveHandle);
            }
            this.autosaveHandle = window.setTimeout(() => this.save(false), 1500);
        });

        this.route.paramMap.subscribe(params => {
            const id = Number(params.get('eb_id') || 0);
            if (id > 0) {
                this.load(id);
            } else {
                this.messageService.hintMessage(
                    'Bitte legen Sie einen Evaluationsbogen über einen TSK-bezahlten Biosicherheitsplan an.',
                );
                this.router
                    .navigate(['belege/bestandsbesuch'], { queryParams: { tab: 'evaluationsbogen-schwein' } })
                    .catch(() => {});
            }
        });
    }

    ngOnDestroy(): void {
        this.formSub?.unsubscribe();
        if (this.autosaveHandle) {
            window.clearTimeout(this.autosaveHandle);
        }
    }

    isReadOnly(): boolean {
        return this.statusId === 232 || this.statusId === 250 || this.statusId === 251;
    }

    isAlreadySigned(): boolean {
        return this.unterschriftKunde;
    }

    isBeraterAlreadySigned(): boolean {
        return this.unterschriftBerater;
    }

    hasBerater(): boolean {
        const name = this.form.get('berater.name')?.value;
        return !!(name && String(name).trim());
    }

    showHandlungsempfehlungen(): boolean {
        const um = this.form.get('handlungsempfehlungenUmgesetzt')?.value;
        const zus = this.form.get('zusaetzlicheEmpfehlungen')?.value;
        return um === false || zus === true;
    }

    private getCurrentMandantName(): string {
        try {
            const m = this.localStorageService.getCurrentMandant();
            return (m?.bezeichnung || '').trim();
        } catch {
            return '';
        }
    }

    private getCurrentMandantVvvo(): string {
        try {
            const m = this.localStorageService.getCurrentMandant();
            return (m?.vvvo || '').trim();
        } catch {
            return '';
        }
    }

    /** Praxis = Mandant; bei force auch überschreiben. */
    private ensurePraxisFromMandant(force: boolean): void {
        const ctrl = this.form.get('tierarzt.praxis');
        if (!ctrl) {
            return;
        }
        const current = String(ctrl.value || '').trim();
        if (!force && current) {
            return;
        }
        const name = this.getCurrentMandantName();
        if (name) {
            ctrl.setValue(name, { emitEvent: false });
        }
    }

    /** Registriernummer Praxis = Mandanten-VVVO. */
    private ensureTierarztRegFromMandant(force: boolean): void {
        const ctrl = this.form.get('tierarzt.registriernummern');
        if (!ctrl) {
            return;
        }
        const current = String(ctrl.value || '').trim();
        if (!force && current) {
            return;
        }
        const vvvo = this.getCurrentMandantVvvo();
        if (vvvo) {
            ctrl.setValue(vvvo, { emitEvent: false });
        }
    }

    getStatusLabel(): string {
        if (this.statusId === 231) {
            return 'Erstellt';
        }
        if (this.statusId === 232) {
            return 'Abgeschlossen';
        }
        if (this.statusId === 250) {
            return 'TSK beantragt';
        }
        if (this.statusId === 251) {
            return 'TSK bezahlt';
        }
        return String(this.statusId);
    }

    addHandlungRow(): void {
        this.handlungsempfehlungen.push(this.createHandlungRow());
    }

    removeHandlungRow(index: number): void {
        if (this.handlungsempfehlungen.length <= 1) {
            return;
        }
        this.handlungsempfehlungen.removeAt(index);
    }

    setTierarzt(vet: Tierarzt): void {
        const nextId = vet?.tierarzt_id ?? null;
        if ((this.tierarztId ?? null) === nextId) {
            return;
        }
        this.currentVet = vet || null;
        this.tierarztId = nextId;
        if (vet) {
            const name = `${vet.vorname || ''} ${vet.nachname || ''}`.trim();
            this.form.get('tierarzt.name')?.setValue(name);
            this.ensurePraxisFromMandant(true);
            this.ensureTierarztRegFromMandant(true);
            const reg = (vet as any)?.vvvo || (vet as any)?.registriernummer || '';
            if (reg && !this.form.get('tierarzt.registriernummern')?.value) {
                this.form.get('tierarzt.registriernummern')?.setValue(reg);
            }
        }
        if (!this.isLoading) {
            this.save(true);
        }
    }

    openPlanUpdateFilePicker(input: HTMLInputElement): void {
        input.click();
    }

    onPlanUpdateFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input?.files?.length) {
            this.addPlanUpdateFiles(input.files);
            input.value = '';
        }
    }

    onPlanUpdateDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    onPlanUpdateDrop(event: DragEvent): void {
        event.preventDefault();
        if (event.dataTransfer?.files?.length) {
            this.addPlanUpdateFiles(event.dataTransfer.files);
        }
    }

    openPlanUpdateDoc(entry: { bsd_id?: number; dateiname: string }): void {
        if (!entry?.bsd_id) {
            return;
        }
        const input = new PdfViewerDialogInput(
            'evaluationsbogen_schwein_dokuments/read_or_mail_pdf.php',
            new HttpParams().set('bsd_id', String(entry.bsd_id)),
            entry.dateiname || 'Anlage',
        );
        this.pdf.openPdfViewer(input);
    }

    removePlanUpdateDoc(event: Event, index: number): void {
        event?.preventDefault();
        event?.stopPropagation();
        const item = this.planUpdateUploads[index];
        if (!item) {
            return;
        }
        if (!item.bsd_id) {
            this.planUpdateUploads.splice(index, 1);
            return;
        }

        const input = new ConfirmDialogInput();
        input.headline = 'Datei löschen';
        input.text = 'Möchten Sie die Datei wirklich löschen?';
        input.okButton = 'Löschen';
        input.cancelButton = 'Abbrechen';
        this.dialog
            .open(ConfirmDialogComponent, { data: input })
            .afterClosed()
            .subscribe((result: ConfirmDialogResult) => {
                if (!(result?.ok && result?.confirmed)) {
                    return;
                }
                this.api
                    .deleteEntity(this.apiBaseUrl + 'evaluationsbogen_schwein_dokuments/delete.php', {
                        bsd_id: item.bsd_id,
                    })
                    .subscribe({
                        next: () => {
                            this.planUpdateUploads = this.planUpdateUploads.filter((_, i) => i !== index);
                            this.messageService.infoMessage('Datei wurde gelöscht.');
                            this.loadPlanUpdateUploads();
                        },
                        error: err =>
                            this.messageService.errorMessage('Dokument konnte nicht gelöscht werden.', err),
                    });
            });
    }

    abschliessen(): void {
        const input = new ConfirmDialogInput();
        input.headline = 'Evaluationsbogen abschließen';
        input.text = 'Soll der Evaluationsbogen abgeschlossen und das PDF erzeugt werden?';
        input.okButton = 'Abschließen';
        input.cancelButton = 'Abbrechen';
        this.dialog
            .open(ConfirmDialogComponent, { data: input })
            .afterClosed()
            .subscribe({
                next: (response: ConfirmDialogResult) => {
                    if (response?.ok && response?.confirmed) {
                        this.pendingSectionIds.add(1);
                        this.pendingSectionIds.add(2);
                        this.pendingSectionIds.add(3);
                        this.save(true, { statusIdForSave: 232, openPdfAfter: true });
                    }
                },
            });
    }

    reopen(): void {
        this.save(true, { statusIdForSave: 231 });
    }

    showPdf(): void {
        if (!this.ebId) {
            return;
        }
        const input = new PdfViewerDialogInput(
            'evaluationsbogen_schwein/read_or_mail_pdf.php',
            new HttpParams().set('eb_id', String(this.ebId)),
            'Evaluationsbogen ' + this.ebId,
            { email: { sende_mail: true, kunde_id: this.kundeId } },
        );
        this.pdf.openPdfViewer(input);
    }

    showVorgaengerPdf(): void {
        if (!this.vorgaengerBsId) {
            this.messageService.hintMessage('Kein Vorgänger-Biosicherheitsplan verknüpft.');
            return;
        }
        const input = new PdfViewerDialogInput(
            'biosicherheitsplan_schwein/read_or_mail_pdf.php',
            new HttpParams().set('bs_id', String(this.vorgaengerBsId)),
            'Biosicherheitsplan ' + this.vorgaengerBsId,
            { email: { sende_mail: true, kunde_id: this.kundeId } },
        );
        this.pdf.openPdfViewer(input);
    }

    back(): void {
        this.router
            .navigate(['belege/bestandsbesuch'], { queryParams: { tab: 'evaluationsbogen-schwein' } })
            .catch(() => {});
    }

    persistKuid(kuid: string): void {
        if (!this.ebId || !kuid) {
            return;
        }
        this.api
            .post(this.apiBaseUrl + 'evaluationsbogen_schwein/update_unterschrift.php', {
                eb_id: this.ebId,
                kuid,
            })
            .subscribe({
                next: () => {
                    this.planKuid = kuid;
                    this.messageService.infoMessage('Unterschrift gespeichert.');
                    this.refreshUnterschriftFlags();
                },
                error: err => this.messageService.errorMessage('Unterschrift fehlgeschlagen.', err),
            });
    }

    onTierhalterSignDialogClosed(): void {
        if (!this.ebId) {
            return;
        }
        const kuid = (this.planKuid || '').trim();
        if (!kuid) {
            return;
        }
        this.api
            .post(this.apiBaseUrl + 'evaluationsbogen_schwein/update_unterschrift.php', {
                eb_id: this.ebId,
                kuid,
            })
            .subscribe({
                next: () => this.refreshUnterschriftFlags(),
                error: () => this.refreshUnterschriftFlags(),
            });
    }

    persistBeraterKuid(kuid: string): void {
        if (!this.hasBerater() || !this.ebId || !kuid) {
            return;
        }
        this.api
            .post(this.apiBaseUrl + 'evaluationsbogen_schwein/update_unterschrift.php', {
                eb_id: this.ebId,
                berater_kuid: kuid,
            })
            .subscribe({
                next: () => {
                    this.planBeraterKuid = kuid;
                    this.messageService.infoMessage('Berater-Unterschrift gespeichert.');
                    this.refreshUnterschriftFlags();
                },
                error: err => this.messageService.errorMessage('Berater-Unterschrift fehlgeschlagen.', err),
            });
    }

    onBeraterSignDialogClosed(): void {
        if (!this.ebId || !this.hasBerater()) {
            return;
        }
        const beraterKuid = (this.planBeraterKuid || '').trim();
        if (!beraterKuid) {
            return;
        }
        this.api
            .post(this.apiBaseUrl + 'evaluationsbogen_schwein/update_unterschrift.php', {
                eb_id: this.ebId,
                berater_kuid: beraterKuid,
            })
            .subscribe({
                next: () => this.refreshUnterschriftFlags(),
                error: () => this.refreshUnterschriftFlags(),
            });
    }

    private refreshUnterschriftFlags(): void {
        if (!this.ebId) {
            return;
        }
        this.api.post(this.apiBaseUrl + 'evaluationsbogen_schwein/read_one.php', { eb_id: this.ebId }).subscribe({
            next: (response: any) => {
                this.planKuid = response?.kuid || this.planKuid;
                this.planBeraterKuid = response?.berater_kuid || this.planBeraterKuid;
                this.unterschriftKunde = Number(response?.unterschrift_kunde || 0) === 1;
                this.unterschriftBerater = Number(response?.unterschrift_berater || 0) === 1;
            },
            error: () => {},
        });
    }

    private addPlanUpdateFiles(fileList: FileList): void {
        if (!this.ebId) {
            this.messageService.hintMessage('Bitte warten, bis der Evaluationsbogen geladen ist.');
            return;
        }
        Array.from(fileList).forEach(file => this.uploadPlanUpdate(file));
    }

    private uploadPlanUpdate(file: File): void {
        const fd = new FormData();
        fd.set('eb_id', String(this.ebId));
        fd.set('sektion', 'plan_update');
        fd.set('dateiname', file.name);
        fd.append('dokument', file);
        const options = { headers: new HttpHeaders('Phito-Type: fileupload'), reportProgress: true };
        this.api
            .postFormData(this.apiBaseUrl + 'evaluationsbogen_schwein_dokuments/create.php', fd, options)
            .subscribe({
                next: (response: any) => {
                    const createdId = Number(response?.bsd_id || 0);
                    if (createdId > 0) {
                        this.planUpdateUploads.unshift({
                            bsd_id: createdId,
                            pdf_id: Number(response?.pdf_id || 0) || undefined,
                            dateiname: file.name,
                            sektion: 'plan_update',
                        });
                    }
                    this.messageService.infoMessage('Dokument wurde hochgeladen.');
                    this.loadPlanUpdateUploads();
                },
                error: err => this.messageService.errorMessage('Dokument konnte nicht hochgeladen werden.', err),
            });
    }

    private loadPlanUpdateUploads(): void {
        if (!this.ebId) {
            return;
        }
        this.api.post(this.apiBaseUrl + 'evaluationsbogen_schwein_dokuments/read.php', { eb_id: this.ebId }).subscribe({
            next: (rows: any) => {
                const list = Array.isArray(rows) ? rows : rows?.records || [];
                this.planUpdateUploads = (list || [])
                    .filter((r: any) => r?.sektion === 'plan_update')
                    .map((r: any) => ({
                        bsd_id: Number(r.bsd_id),
                        pdf_id: Number(r.pdf_id || 0) || undefined,
                        dateiname: r.dateiname || 'Dokument',
                        sektion: r.sektion,
                    }));
            },
            error: () => {
                this.planUpdateUploads = [];
            },
        });
    }

    private ladeTierarzt(id: number | null | undefined): void {
        if (!id || !this.localStorageService.hasPermission(1251)) {
            this.currentVet = null;
            return;
        }
        const params = new HttpParams().set('tierarzt_id', String(id));
        this.api.get(this.apiBaseUrl + 'tierarzt/read_one.php', params).subscribe({
            next: (value: Tierarzt) => {
                this.currentVet = value;
                if (value) {
                    const name = `${value.vorname || ''} ${value.nachname || ''}`.trim();
                    if (name && !this.form.get('tierarzt.name')?.value) {
                        this.form.get('tierarzt.name')?.setValue(name, { emitEvent: false });
                    }
                }
            },
            error: () => {
                this.currentVet = null;
            },
        });
    }

    private load(ebId: number): void {
        this.isLoading = true;
        this.suppressAutosave = true;
        this.api.post(this.apiBaseUrl + 'evaluationsbogen_schwein/read_one.php', { eb_id: ebId }).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                this.ebId = Number(response?.eb_id || ebId);
                this.statusId = Number(response?.status_id || 231);
                this.kundeId = response?.kunde_id != null ? Number(response.kunde_id) : null;
                this.tierarztId = response?.tierarzt_id != null ? Number(response.tierarzt_id) : null;
                this.vorgaengerBsId =
                    response?.vorgaenger_bs_id != null ? Number(response.vorgaenger_bs_id) : null;
                this.planKuid = response?.kuid || null;
                this.planBeraterKuid = response?.berater_kuid || null;
                this.unterschriftKunde = Number(response?.unterschrift_kunde || 0) === 1;
                this.unterschriftBerater = Number(response?.unterschrift_berater || 0) === 1;
                this.patchFromResponse(response);
                this.ensurePraxisFromMandant(false);
                this.ensureTierarztRegFromMandant(false);
                this.ladeTierarzt(this.tierarztId);
                this.loadPlanUpdateUploads();
                this.syncReadOnly();
                setTimeout(() => {
                    this.suppressAutosave = false;
                    this.form.markAsPristine();
                    this.pendingSectionIds.clear();
                    // Leere Praxis/VVVO (ältere Bögen) einmalig mit Mandant nachziehen
                    const s1 = this.parseJson(response?.data_json_1);
                    if (
                        !this.isReadOnly() &&
                        (!String(s1?.tierarzt?.praxis || '').trim() ||
                            !String(s1?.tierarzt?.registriernummern || '').trim()) &&
                        (this.form.get('tierarzt.praxis')?.value ||
                            this.form.get('tierarzt.registriernummern')?.value)
                    ) {
                        this.pendingSectionIds.add(1);
                        this.save(false);
                    }
                });
            },
            error: err => {
                this.isLoading = false;
                this.suppressAutosave = false;
                this.messageService.errorMessage('Evaluationsbogen konnte nicht geladen werden.', err);
            },
        });
    }

    private patchFromResponse(response: any): void {
        const s1 = this.parseJson(response?.data_json_1);
        const s2 = this.parseJson(response?.data_json_2);
        const s3 = this.parseJson(response?.data_json_3);

        this.form.patchValue({
            evaluationsJahr: s1?.evaluationsJahr ?? response?.evaluations_jahr ?? null,
            betrieb: {
                name: s1?.betrieb?.name ?? response?.kunde ?? '',
                vvvo: s1?.betrieb?.vvvo ?? response?.vvvo ?? '',
                tskNummer: s1?.betrieb?.tskNummer ?? response?.tsk_nummer ?? '',
            },
            tierarzt: {
                name: s1?.tierarzt?.name ?? '',
                praxis: s1?.tierarzt?.praxis || this.getCurrentMandantName(),
                registriernummern: s1?.tierarzt?.registriernummern || this.getCurrentMandantVvvo(),
            },
            berater: {
                name: s1?.berater?.name ?? '',
                registriernummern: s1?.berater?.registriernummern ?? '',
            },
            datumBeratung: this.parseDate(s2?.datumBeratung),
            ablaufeLageskizzeAktuell: s2?.ablaufeLageskizzeAktuell ?? null,
            handlungsempfehlungenUmgesetzt: s2?.handlungsempfehlungenUmgesetzt ?? null,
            zusaetzlicheEmpfehlungen: s2?.zusaetzlicheEmpfehlungen ?? null,
            beratungszeit: {
                tierarztStunden: s3?.beratungszeit?.tierarztStunden ?? '',
                beraterStunden: s3?.beratungszeit?.beraterStunden ?? '',
            },
            beihilfeBeantragen: s3?.beihilfeBeantragen ?? true,
        });

        const rows = Array.isArray(s3?.handlungsempfehlungen) ? s3.handlungsempfehlungen : [];
        this.handlungsempfehlungen.clear();
        if (rows.length === 0) {
            this.handlungsempfehlungen.push(this.createHandlungRow());
        } else {
            rows.forEach((row: any) => {
                this.handlungsempfehlungen.push(
                    this.fb.group({
                        nr: [row?.nr ?? ''],
                        schwachstelle: [row?.schwachstelle ?? ''],
                        handlungsempfehlung: [row?.handlungsempfehlung ?? ''],
                        erledigtAm: [this.parseDate(row?.erledigtAm)],
                        unterlagen: [row?.unterlagen ?? ''],
                    }),
                );
            });
        }
    }

    private save(
        force = false,
        options?: { statusIdForSave?: number; openPdfAfter?: boolean },
    ): void {
        if (this.isReadOnly() && options?.statusIdForSave == null) {
            return;
        }
        if (!force && (this.isLoading || this.suppressAutosave || this.isReadOnly())) {
            return;
        }
        if (!this.ebId && !this.kundeId) {
            return;
        }
        if (this.autosaveHandle) {
            window.clearTimeout(this.autosaveHandle);
            this.autosaveHandle = undefined;
        }

        const v = this.form.getRawValue();
        const payload: any = {
            eb_id: this.ebId || 0,
            kunde_id: this.kundeId,
            tierarzt_id: this.tierarztId,
            status_id: options?.statusIdForSave ?? this.statusId ?? 231,
            evaluations_jahr: v.evaluationsJahr,
            vorgaenger_bs_id: this.vorgaengerBsId,
        };

        if (this.pendingSectionIds.has(1) || force) {
            payload.data_json_1 = JSON.stringify({
                evaluationsJahr: v.evaluationsJahr,
                betrieb: v.betrieb,
                tierarzt: v.tierarzt,
                berater: v.berater,
            });
        }
        if (this.pendingSectionIds.has(2) || force) {
            payload.data_json_2 = JSON.stringify({
                datumBeratung: this.formatDate(v.datumBeratung),
                ablaufeLageskizzeAktuell: v.ablaufeLageskizzeAktuell,
                handlungsempfehlungenUmgesetzt: v.handlungsempfehlungenUmgesetzt,
                zusaetzlicheEmpfehlungen: v.zusaetzlicheEmpfehlungen,
            });
        }
        if (this.pendingSectionIds.has(3) || force) {
            payload.data_json_3 = JSON.stringify({
                handlungsempfehlungen: (v.handlungsempfehlungen || []).map((row: any) => ({
                    ...row,
                    erledigtAm: this.formatDate(row?.erledigtAm),
                })),
                beratungszeit: v.beratungszeit,
                beihilfeBeantragen: v.beihilfeBeantragen,
            });
        }

        this.isSaving = true;
        this.api.post(this.apiBaseUrl + 'evaluationsbogen_schwein/update.php', payload).subscribe({
            next: (response: any) => {
                this.isSaving = false;
                if (response?.eb_id) {
                    this.ebId = Number(response.eb_id);
                }
                if (options?.statusIdForSave != null) {
                    this.statusId = options.statusIdForSave;
                } else if (response?.status_id != null) {
                    this.statusId = Number(response.status_id);
                }
                this.syncReadOnly();
                this.pendingSectionIds.clear();
                this.form.markAsPristine();
                if (options?.openPdfAfter) {
                    this.showPdf();
                }
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Speichern fehlgeschlagen.', err);
            },
        });
    }

    private syncReadOnly(): void {
        if (this.isReadOnly()) {
            this.form.disable({ emitEvent: false });
        } else {
            this.form.enable({ emitEvent: false });
        }
    }

    private createHandlungRow(): FormGroup {
        return this.fb.group({
            nr: [''],
            schwachstelle: [''],
            handlungsempfehlung: [''],
            erledigtAm: [null as Date | null],
            unterlagen: [''],
        });
    }

    private parseDate(value: unknown): Date | null {
        if (value == null || value === '') {
            return null;
        }
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }
        const raw = String(value).trim();
        if (!raw) {
            return null;
        }
        // yyyy-mm-dd oder ISO
        const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
        if (iso) {
            const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
            return Number.isNaN(d.getTime()) ? null : d;
        }
        // dd.mm.yyyy
        const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(raw);
        if (de) {
            const d = new Date(Number(de[3]), Number(de[2]) - 1, Number(de[1]));
            return Number.isNaN(d.getTime()) ? null : d;
        }
        const fallback = new Date(raw);
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    }

    private formatDate(value: unknown): string {
        if (value == null || value === '') {
            return '';
        }
        const d = value instanceof Date ? value : this.parseDate(value);
        if (!d) {
            return '';
        }
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    private parseJson(raw: any): any {
        if (!raw) {
            return {};
        }
        if (typeof raw === 'object') {
            return raw;
        }
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    }
}
